import { Dialog } from "./Dialog";

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

export function HelpDialog({ isOpen, onClose }: Props) {
  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      title="Welcome to TaxChat"
      description="Your AI-powered tax return assistant"
      size="lg"
    >
      <div className="space-y-6 text-sm">
        {/* What is TaxChat */}
        <section>
          <h3 className="font-semibold text-base mb-2">What is TaxChat?</h3>
          <p className="text-(--color-text-secondary) leading-relaxed">
            TaxChat is an AI-powered tool that helps you understand and analyze tax returns.
            Upload PDF tax documents and get instant insights, comparisons across years,
            and answers to your tax questions.
          </p>
        </section>

        {/* How to Use */}
        <section>
          <h3 className="font-semibold text-base mb-2">How to Use</h3>
          <ol className="text-(--color-text-secondary) leading-relaxed space-y-2 list-decimal list-inside">
            <li>
              <strong>Upload tax returns</strong> — Click "Get Started" and drop your PDF tax documents
            </li>
            <li>
              <strong>View insights</strong> — See income, taxes, and deductions at a glance
            </li>
            <li>
              <strong>Compare years</strong> — Switch between years using the tabs, or view "All time" summary
            </li>
            <li>
              <strong>Ask questions</strong> — Use the Chat feature to ask anything about your taxes
            </li>
          </ol>
        </section>

        {/* What Can You Ask */}
        <section>
          <h3 className="font-semibold text-base mb-2">What Can You Ask?</h3>
          <p className="text-(--color-text-secondary) mb-3">
            The AI assistant can help you with questions like:
          </p>
          <ul className="text-(--color-text-secondary) leading-relaxed space-y-1.5">
            <li className="flex gap-2">
              <span className="text-(--color-brand)">•</span>
              "Help me understand my tax returns"
            </li>
            <li className="flex gap-2">
              <span className="text-(--color-brand)">•</span>
              "Why did my taxes increase this year?"
            </li>
            <li className="flex gap-2">
              <span className="text-(--color-brand)">•</span>
              "What deductions am I claiming?"
            </li>
            <li className="flex gap-2">
              <span className="text-(--color-brand)">•</span>
              "How can I optimize for next year?"
            </li>
            <li className="flex gap-2">
              <span className="text-(--color-brand)">•</span>
              "Look for mistakes in my tax return history"
            </li>
            <li className="flex gap-2">
              <span className="text-(--color-brand)">•</span>
              "Compare my federal vs state taxes"
            </li>
          </ul>
        </section>

        {/* Privacy Note */}
        <section className="bg-(--color-bg-subtle) rounded-lg p-4">
          <h3 className="font-semibold text-base mb-2">Privacy & Security</h3>
          <p className="text-(--color-text-secondary) leading-relaxed">
            Your tax documents are processed securely using Claude AI.
            Data is stored locally and is not shared with third parties.
            You can reset all data at any time from the menu.
          </p>
        </section>

        {/* Support */}
        <section>
          <h3 className="font-semibold text-base mb-2">Need Help?</h3>
          <p className="text-(--color-text-secondary) leading-relaxed">
            If you have questions or need assistance, contact your LeCPA representative.
          </p>
        </section>
      </div>
    </Dialog>
  );
}
